import React, { useEffect, useState } from 'react';
import ActivitySection from './ActivitySection';
import ChatConversations from './ChatConversations';
import LineChart from './charts/LineChart';
import SectionState from './SectionState';
import { Collapse } from './ScoreReadout';
import { EM_DASH } from '../../../lib/format';
import {
  dayMonth, otherActivePlans, plural,
} from './lib/activityFormat';

const PLAN_STATUS = new Set(['Active', 'Draft', 'Completed', 'Archived']);
const REVIEW_DECISION = new Set(['Continue', 'Revise']);

const word = (value, allowed) => (allowed.has(value) ? value : null);

const CHAT_DESCRIPTION = 'How much the student uses the chat and what for. Usage, the study plan, and every conversation in full.';

/**
 * How much this student uses the chat, what for, and every conversation.
 * The section is ready at zero: an empty list is not an empty section.
 * `embedded` draws the same content under a Chat subtitle, for Applications,
 * instead of its own Activity section. Conversations reset with studentId.
 */
const ChatSection = ({ state, studentId, embedded = false }) => {
  const data = state.status === 'ready' ? state.data : null;
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [studentId]);
  const panelId = 'td-act-chat-detail';

  let days = null;
  if (data) {
    days = data.days_in_period == null
      ? String(data.active_days)
      : `${data.active_days} of ${data.days_in_period}`;
  }

  const body = (
    <>
      <SectionState state={state} what="chat" />
      {data ? (
        <>
          <div className="td-act-statrow">
            <div className="td-stat">
              <span className="td-stat__eyebrow">Active days</span>
              <span className="td-stat__value">{days}</span>
            </div>
            <div className="td-stat">
              <span className="td-stat__eyebrow">Messages</span>
              <span className="td-stat__value">{data.messages}</span>
              <span className="td-stat__caption">{plural(data.conversations, 'conversation')}</span>
            </div>
            <div className="td-stat">
              <span className="td-stat__eyebrow">Last used</span>
              <span className="td-stat__value">{data.last_used_at ? dayMonth(data.last_used_at) : EM_DASH}</span>
            </div>
          </div>
          <Collapse id={panelId} open={open} onToggle={() => setOpen((value) => !value)} label="Details">
            <WeekChart weeks={data.weeks} />
            <ModeMix modes={data.modes} />
            <Plan plan={data.plan} />
          </Collapse>
        </>
      ) : null}
      {studentId ? <ChatConversations studentId={studentId} /> : null}
    </>
  );

  if (embedded) {
    return (
      <div id="td-act-chat">
        <h4 id="td-act-chat-title" className="td-act-subtitle" tabIndex={-1}>Chat</h4>
        <p className="td-act-section__sub">{CHAT_DESCRIPTION}</p>
        {body}
      </div>
    );
  }

  return (
    <ActivitySection id="chat" title="Chat" description={CHAT_DESCRIPTION}>
      {body}
    </ActivitySection>
  );
};

const WeekChart = ({ weeks }) => {
  const points = (weeks || []).map((week) => ({
    key: week.week,
    label: dayMonth(week.week),
    value: week.messages,
  }));
  if (!points.length) return null;
  return (
    <div>
      <div className="td-act-subhead">Messages over the last eight weeks</div>
      <LineChart
        points={points}
        format={(value) => String(value)}
        ariaLabel={`Messages by week: ${points.map((point) => `${point.label} ${point.value}`).join(', ')}`}
      />
    </div>
  );
};

const ModeMix = ({ modes }) => {
  if (!modes || !modes.length) return <p className="td-act-meta">No messages in this period, so no mix.</p>;
  return (
    <ul className="td-act-modes">
      {modes.map((mode) => (
        <li key={mode.label}>{`${mode.label} ${mode.share}%`}</li>
      ))}
    </ul>
  );
};

const Plan = ({ plan }) => {
  if (!plan) return <p className="td-act-meta">No study plan yet.</p>;
  const status = word(plan.status, PLAN_STATUS);
  const others = otherActivePlans(plan.other_active_plans);
  const decision = plan.review ? word(plan.review.decision, REVIEW_DECISION) : null;
  const tasks = plan.tasks || {};
  return (
    <div>
      <div className="td-act-subhead">{plan.title}</div>
      {status ? <p className="td-act-meta">{status}</p> : null}
      {others ? <p className="td-act-meta">{others}</p> : null}
      <p className="td-act-meta">
        {`${tasks.done || 0} done · ${tasks.skipped || 0} skipped · ${tasks.overdue || 0} overdue · ${tasks.total || 0} tasks`}
      </p>
      {decision ? (
        <p className="td-act-meta">{`${decision}${plan.review.at ? ` · ${dayMonth(plan.review.at)}` : ''}`}</p>
      ) : null}
      {plan.phase ? <p className="td-act-meta">{plan.phase}</p> : null}
      {plan.next_task ? <p className="td-act-meta">{`Next: ${plan.next_task}`}</p> : null}
    </div>
  );
};

export default ChatSection;
